import { faInfoCircle } from "@fortawesome/pro-regular-svg-icons";

import { FunctionComponent, useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

import { classNames } from "@/utils/classNames";

import { useViewportClampedPanel } from "@/utils/useViewportClampedPanel";

import { Icon } from "../Icon/Icon";

export const Information: FunctionComponent<{
	information: string | JSX.Element;
	className?: string;
	iconClassName?: string;
	/** Portal target for the tooltip (extension passes its shadow root); defaults to `document.body`. */
	portalContainer?: HTMLElement | null;
}> = ({ information, className, iconClassName, portalContainer }) => {
	const [showInformation, setShowInformation] = useState(false);

	const selectFieldRef = useRef<HTMLDivElement>(null);

	const dropdownRef = useRef<HTMLDivElement>(null);

	// Keeps the portalled panel inside the viewport wherever the trigger sits (PREC-4138).
	useViewportClampedPanel(dropdownRef, showInformation);
	const [dropdownStyle, setDropdownStyle] = useState<React.CSSProperties>({});

	const updateDropdownPosition = useCallback(() => {
		if (selectFieldRef.current) {
			const rect = selectFieldRef.current.getBoundingClientRect();
			const dropdownRec = dropdownRef.current?.getBoundingClientRect();
			const tooltipHeight = dropdownRec?.height ?? 0;
			const gap = 8;

			// Viewport-relative `fixed` coords for both hosts. The extension's shadow container
			// isn't the scroll context, so scroll offsets push the tooltip offscreen there; and on
			// the web an absolutely positioned body portal adds its box to the document's
			// scrollable height, which turns the whole app into a scrollable page (PREC-4114).
			// Default position: above the icon. Flip below if it would overflow the top of the viewport.
			const wouldOverflowTop = rect.top - tooltipHeight - gap < 0;
			const top = wouldOverflowTop ? rect.bottom + gap : rect.top - tooltipHeight - gap;

			setDropdownStyle({
				position: "fixed",
				top,
				left: rect.left,
			});
		}
	}, [portalContainer]);

	useEffect(() => {
		if (showInformation) {
			updateDropdownPosition();
			window.addEventListener("resize", updateDropdownPosition);
		} else {
			window.removeEventListener("resize", updateDropdownPosition);
		}

		return () => {
			window.removeEventListener("resize", updateDropdownPosition);
		};
	}, [showInformation, updateDropdownPosition]);

	return (
		<span className="group cursor-pointer inline-flex relative -top-0.5">
			<div ref={selectFieldRef} className="flex flex-row items-center text-text-meta">
				<Icon
					className={classNames(
						iconClassName || "text-text-disabled w-3 h-3 group-hover:text-brand-light"
					)}
					icon={faInfoCircle}
					onMouseEnter={() => setShowInformation(true)}
					onMouseLeave={() => setShowInformation(false)}
				/>
				{showInformation &&
					createPortal(
						<p
							ref={dropdownRef}
							className={classNames(
								// Positioned `fixed` from the inline style below (web rule §11) — the
								// class list must not carry a conflicting `absolute`.
								"transform -translate-x-1/2 w-[200px] bg-white shadow-all p-3 rounded-md text-xs z-[10000] group-hover:block"
							)}
							style={dropdownStyle}
						>
							{information}
						</p>,
						portalContainer ?? document.body
					)}
			</div>
		</span>
	);
};
